import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { computePosition, flip, offset, shift, size } from "@floating-ui/react-dom"
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext"
import { LexicalTypeaheadMenuPlugin, useBasicTypeaheadTriggerMatch } from "@lexical/react/LexicalTypeaheadMenuPlugin"
import { $createTextNode, $getRoot, $isElementNode, LexicalNode, TextNode } from "lexical"
import { createPortal } from "react-dom"

import { useGlobalSearch } from "@/hooks/use-global-search"
import { useHouseholdNoteMentionables } from "@/hooks/use-household-note-mentionables"
import { $createMentionNode, MentionNode } from "@/components/lexical/nodes/mention/node"

import { MentionDropdown } from "./mention-dropdown"
import { MentionDropdownPluginProps, MentionOption, MentionRecord, MentionRecordType } from "./types"

function collectMentionsFromEditor(): MentionRecord[] {
  const mentions: MentionRecord[] = []
  const root = $getRoot()

  const traverseNodes = (node: LexicalNode) => {
    if (node instanceof MentionNode) {
      mentions.push({
        id: node.__recordId,
        label: node.__text,
        recordType: node.__recordType as MentionRecordType,
        mentionableRecord: node.__mentionableRecord,
      })
    }

    if ($isElementNode(node)) {
      const children = node.getChildren()
      for (let i = 0; i < children.length; i++) {
        traverseNodes(children[i])
      }
    }
  }

  traverseNodes(root)
  return mentions
}

export function MentionDropdownPlugin({
  mentionableTypes,
  scopeUsersToBook,
  bookId,
  teamOptions,
  options,
  householdId,
  onMentionAdded,
  onMentionRemoved,
  onOpenChange,
}: MentionDropdownPluginProps) {
  const [editor] = useLexicalComposerContext()
  const [menuPortal, setMenuPortal] = useState<HTMLElement | null>(null)
  const menuRef = useRef<HTMLDivElement | null>(null)
  const anchorRef = useRef<HTMLElement | null>(null)
  const onOpenChangeRef = useRef(onOpenChange)
  const { results, handleSearchChange, isSearching, searchQuery } = useGlobalSearch({
    allowedTypes: mentionableTypes,
    bookScopedUsers: scopeUsersToBook,
    bookIdOverride: options ? "" : bookId,
  })
  const { filterMentionables } = useHouseholdNoteMentionables(householdId)

  const mentionOptions: MentionOption[] = useMemo(() => {
    if (options)
      return options.filter((option) => option.label.toLowerCase().includes(searchQuery.trim().toLowerCase()))

    const householdResults = filterMentionables(searchQuery)
    const seen = new Set(householdResults.map((result) => `${result.type}-${result.id}`))
    const merged = [...householdResults, ...results.filter((result) => !seen.has(`${result.type}-${result.id}`))]
    const matchingTeams =
      teamOptions?.filter((option) => option.label.toLowerCase().includes(searchQuery.trim().toLowerCase())) ?? []
    return [...matchingTeams, ...merged.map((result) => new MentionOption(result))]
  }, [filterMentionables, results, searchQuery, teamOptions, options])

  const checkForTriggerMatch = useBasicTypeaheadTriggerMatch("@", {
    minLength: 0,
    maxLength: 50,
  })

  useEffect(() => {
    onOpenChangeRef.current = onOpenChange
  }, [onOpenChange])

  const handleMenuOpenChange = useCallback((open: boolean) => onOpenChangeRef.current?.(open), [])

  useEffect(() => {
    const menuElement = document.createElement("div")
    menuElement.className = "typeahead-menu-portal"
    Object.assign(menuElement.style, {
      position: "fixed",
      left: "0px",
      top: "0px",
      zIndex: "9999",
    })
    document.body.appendChild(menuElement)
    setMenuPortal(menuElement)

    return () => {
      if (document.body.contains(menuElement)) {
        document.body.removeChild(menuElement)
      }
    }
  }, [])

  const positionMenu = useCallback(() => {
    const portalEl = menuRef.current
    const anchorEl = anchorRef.current
    const menuEl = portalEl?.firstElementChild
    if (!(menuEl instanceof HTMLElement) || !anchorEl) return

    const selection = window.getSelection()
    const caretRect = selection?.rangeCount ? selection.getRangeAt(0).getBoundingClientRect() : null
    const rect = caretRect?.height ? caretRect : anchorEl.getBoundingClientRect()

    void computePosition(
      { getBoundingClientRect: () => rect, contextElement: editor.getRootElement() ?? undefined },
      menuEl,
      {
        strategy: "fixed",
        placement: "bottom-start",
        middleware: [
          offset(4),
          flip({ padding: 8 }),
          shift({ padding: 8 }),
          size({
            padding: 8,
            apply({ availableHeight, elements }) {
              elements.floating.style.setProperty(
                "--mention-menu-max-height",
                `${Math.max(0, Math.min(300, availableHeight - 2))}px`
              )
            },
          }),
        ],
      }
    ).then(({ x, y }) => {
      Object.assign(menuEl.style, { left: `${x}px`, top: `${y}px` })
    })
  }, [editor])

  useEffect(() => {
    const handleReposition = () => positionMenu()
    window.addEventListener("scroll", handleReposition, true)
    window.addEventListener("resize", handleReposition)
    return () => {
      window.removeEventListener("scroll", handleReposition, true)
      window.removeEventListener("resize", handleReposition)
    }
  }, [positionMenu])

  useEffect(() => {
    if (!onMentionAdded && !onMentionRemoved) {
      return
    }

    const mentionsMap = new Map<string, MentionRecord>()

    const updateMentionsAndNotify = () => {
      const currentMentions = collectMentionsFromEditor()
      const currentMentionsMap = new Map<string, MentionRecord>()

      currentMentions.forEach((mention) => {
        const key = `${mention.recordType}-${mention.id}`
        currentMentionsMap.set(key, mention)

        if (onMentionAdded && !mentionsMap.has(key)) {
          onMentionAdded(mention)
        }
      })

      if (onMentionRemoved) {
        mentionsMap.forEach((mention, key) => {
          if (!currentMentionsMap.has(key)) {
            onMentionRemoved(mention)
          }
        })
      }

      return currentMentionsMap
    }

    const removeUpdateListener = editor.registerUpdateListener(() => {
      editor.getEditorState().read(() => {
        const newMentionsMap = updateMentionsAndNotify()
        mentionsMap.clear()
        newMentionsMap.forEach((value, key) => {
          mentionsMap.set(key, value)
        })
      })
    })

    editor.getEditorState().read(() => {
      const currentMentions = collectMentionsFromEditor()
      currentMentions.forEach((mention) => {
        const key = `${mention.recordType}-${mention.id}`
        mentionsMap.set(key, mention)
      })
    })

    return removeUpdateListener
  }, [editor, onMentionAdded, onMentionRemoved])

  const onSelectOption = useCallback(
    (selectedOption: MentionOption, nodeToReplace: TextNode | null, closeMenu: () => void) => {
      editor.update(() => {
        let normalizedRecordType = selectedOption.recordType
        if (selectedOption.recordType === "Client" || selectedOption.recordType === "Prospect") {
          normalizedRecordType = "Household"
        }

        // Extract mentionable_record from Person and Trust search results
        const mentionableRecord =
          (selectedOption.searchResult.type === "person" || selectedOption.searchResult.type === "trust") &&
          "mentionable_record" in selectedOption.searchResult.attributes &&
          selectedOption.searchResult.attributes.mentionable_record
            ? selectedOption.searchResult.attributes.mentionable_record
            : undefined

        const replacementNode = $createMentionNode(
          selectedOption.label,
          normalizedRecordType,
          selectedOption.recordId,
          mentionableRecord
        )

        if (nodeToReplace) {
          nodeToReplace.replace(replacementNode)
        }

        if (nodeToReplace && replacementNode instanceof MentionNode) {
          const spaceNode = $createTextNode(" ")
          replacementNode.insertAfter(spaceNode)
          spaceNode.select()
        } else {
          replacementNode.select()
        }

        closeMenu()
      })
    },
    [editor]
  )

  return menuPortal ? (
    <>
      {createPortal(<div ref={menuRef} />, menuPortal)}
      <LexicalTypeaheadMenuPlugin<MentionOption>
        onOpen={() => handleMenuOpenChange(true)}
        onClose={() => handleMenuOpenChange(false)}
        onQueryChange={(q) => {
          if (q !== null) {
            handleSearchChange(q)
          }
        }}
        onSelectOption={onSelectOption}
        triggerFn={checkForTriggerMatch}
        options={mentionOptions}
        menuRenderFn={(anchorElementRef, { selectedIndex, selectOptionAndCleanUp }) => {
          anchorRef.current = anchorElementRef.current as HTMLElement | null
          requestAnimationFrame(() => requestAnimationFrame(positionMenu))

          const isOpen = !!(anchorElementRef.current && menuRef.current)

          return isOpen
            ? createPortal(
                <MentionDropdown
                  options={mentionOptions}
                  selectedIndex={selectedIndex ?? 0}
                  searching={options ? false : isSearching}
                  searchQuery={searchQuery}
                  onOpenChange={handleMenuOpenChange}
                  onSelect={(option) => {
                    selectOptionAndCleanUp(option)
                  }}
                />,
                menuRef.current!
              )
            : null
        }}
        anchorClassName="mention-dropdown-anchor"
      />
    </>
  ) : null
}

export type { MentionRecord }
